import { de as LocaleDe } from 'date-fns/locale/de';
import { format } from 'date-fns/format';
import isEqual from 'lodash/isEqual';
import type {
  DetailedHTMLProps,
  FocusEvent,
  HTMLAttributes,
  KeyboardEvent,
} from 'react';

import { Runtime, SearchFormStateProps } from '@silvertours/front-entities';
import { Session, String } from '@silvertours/front-shared';
import {
  DeleteButtonContent,
  SearchHistoryDate,
  SearchHistoryHeadline,
  SearchHistoryListItem,
  SearchHistoryWrapper,
} from './SearchHistory.styles';

// interface design see https://silvertours.atlassian.net/wiki/spaces/PAGES/pages/2239758455/Search+form+Search#Search-history
export interface SearchHistoryType {
  items: Array<SearchHistoryItem>;
}

export type SearchHistoryItem = {
  depName: string; // example: "Berlin"
  depAirportCode: string; // example: "BER"
  depDate: string; // example: "2022-06-20"
  depWeekDay: string; // probably obsolete, example: "Montag"
  depTime: string; // example: "08:00"
  destName: string; // example: "Dresden"
  destAirportCode: string; // example: "DRS"
  destDate: string; // example: "2022-06-26"
  destWeekDay: string; // probably obsolete, example: "Sonntag"
  destTime: string; // example: "17:00"
  imageUrl: string; // example: "//image.billiger-mietwagen.de/bm/global/files/images/Stage-Bilder_Berlin.jpg"
  language: string; // example: "it"
  market?: string; // example: "IT"
};

const SEARCH_HISTORY_SESSION_STORAGE_KEY = 'SearchHistoryKey8934087340';
const SEARCH_HISTORY_LIMIT = 6;

export const getSearchHistory = (): SearchHistoryType => {
  const data = Session.SessionStorageManager.getData(
    SEARCH_HISTORY_SESSION_STORAGE_KEY,
  );

  if (data) {
    return data;
  }
  return { items: [] };
};

export const mapFormToSearchItem = (formBody: SearchFormStateProps) => ({
  depAirportCode:
    formBody.depLocation?.iataCode ?? formBody.depLocation?.cityCode ?? '',
  destAirportCode:
    formBody.destLocation?.iataCode ?? formBody.destLocation?.cityCode ?? '',
  destName: formBody.destLocation?.name ?? '',
  depName: formBody.depLocation?.name ?? '',
  depDate: formBody.depDate.split('T')[0],
  depTime: formBody.depDate.split('T')[1],
  destDate: formBody.destDate.split('T')[0],
  destTime: formBody.destDate.split('T')[1],
  depWeekDay: '',
  destWeekDay: '',
  imageUrl: '', // TODO: implement background image from suggestions API into Stage on location click.
});

export const addItemToSearchHistory = (item: SearchHistoryItem) => {
  const history: SearchHistoryType = getSearchHistory();
  if (!history.items.find(storedItem => isEqual(storedItem, item))) {
    history.items.unshift(item);
    if (history.items.length > SEARCH_HISTORY_LIMIT) {
      history.items.pop();
    }
    Session.SessionStorageManager.setData(
      SEARCH_HISTORY_SESSION_STORAGE_KEY,
      history,
    );
  }
};

export const clearSearchHistory = () => {
  Session.SessionStorageManager.removeData(SEARCH_HISTORY_SESSION_STORAGE_KEY);
};

interface SearchHistoryProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  clearSearchHistoryCallback: () => void;
  deleteLabel: string;
  handleItemClick: (value: SearchHistoryItem) => void;
  onLastElementBlur: (event: FocusEvent<HTMLButtonElement, Element>) => void;
  handleKeyDown: (
    event: KeyboardEvent<HTMLElement>,
    handleEnterKey: () => void,
  ) => void;
  headline: string;
  hidden: boolean;
  history: SearchHistoryType;
}

const SearchHistory = ({
  clearSearchHistoryCallback,
  deleteLabel,
  hidden,
  onLastElementBlur,
  handleKeyDown,
  handleItemClick,
  headline,
  history,
  ...props
}: SearchHistoryProps) => {
  const { language } = Runtime.useLocale();

  const localDateFormats = String.dateFormats[language];

  const filteredItems = history.items?.filter(
    item => item.language === language || item.language === undefined,
  );

  return (
    <SearchHistoryWrapper
      {...props}
      hidden={hidden || filteredItems?.length === 0}
    >
      <SearchHistoryHeadline textColor="grey">{headline}</SearchHistoryHeadline>
      <ul className="mb-50 pt-20">
        {filteredItems?.map((item, index) => (
          <li
            // We pass the index as part of the key since we are not modifying this list on the same render.
            // eslint-disable-next-line react/no-array-index-key
            key={item.depName + index}
            className="border-b-color8 py-30 px-50 hover:bg-color8 cursor-pointer border-b-[1px]"
          >
            <button
              type="button"
              onClick={() => handleItemClick(item)}
              onKeyDown={event => {
                handleKeyDown(event, () => handleItemClick(item));
              }}
              className="searchHistory w-full text-left"
            >
              <SearchHistoryListItem inline>
                {item.depName} {item.destName ? ` - ${item.destName}` : ''}
              </SearchHistoryListItem>
              <SearchHistoryDate inline textColor="grey">
                {format(new Date(item.depDate), localDateFormats.dateLong, {
                  locale: LocaleDe,
                })}{' '}
                -{' '}
                {format(new Date(item.destDate), localDateFormats.dateLong, {
                  locale: LocaleDe,
                })}
              </SearchHistoryDate>
            </button>
          </li>
        ))}
      </ul>
      {/* using onMouseDown on the input to guarantee that the button is still rendered before losing focus, triggering the event */}
      <button
        type="button"
        onMouseDown={clearSearchHistoryCallback}
        onKeyDown={event => handleKeyDown(event, clearSearchHistoryCallback)}
        onBlur={onLastElementBlur}
        className="text-color1 hover:text-color1-800 focus-visible:outline-color3 active:text-color1-900 rounded-sm focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-offset-4"
      >
        <DeleteButtonContent>{deleteLabel}</DeleteButtonContent>
      </button>
    </SearchHistoryWrapper>
  );
};

export { SearchHistory };
